export const normalizeStudentSignupPayload = ({
  data,
  roleId,
  dob,
  email_verify_string,
  selectedResume,
}) => {
  let payload = { ...data };

  payload.role_id = roleId;
  payload.step_number = "step_2";
  payload.type = "register";
  payload.role_type = roleId;
  payload.email_verify_string = email_verify_string;

  if (payload.subarea) {
    payload.subareas = payload.subarea;
  }

  if (payload.mainArea) {
    payload.domains = payload.mainArea;
  }

  payload = normalizeBooleanFields(payload);
  payload = normalizeDateFields(payload, dob);
  payload = cleanupPayloadFields(payload);

  return handleResumeInPayload(payload, selectedResume);
};

const normalizeBooleanFields = (payload) => {
  const booleanKeys = [
    "mn_open_to_talk",
    "is_privacy",
    "is_terms",
    "my_consent",
    "mn_loking_for_coach",
    "mf_mental_health",
    "mf_certified_coach",
    "lgbtq_discussion",
    "is_disclaimer",
    "training_video",
  ];

  const newPayload = { ...payload };
  for (const key of booleanKeys) {
    if (key in newPayload) {
      newPayload[key] = newPayload[key] ? 1 : 0;
    }
  }
  return newPayload;
};

const normalizeDateFields = (payload, dob) => {
  const newPayload = { ...payload };
  if ("dob" in newPayload) {
    newPayload.dob = newPayload.dob ? dob : "";
  }

  if (newPayload.availability) {
    newPayload.availability = newPayload.availability ? 2 : 1;
  } else {
    newPayload.availability = 1;
    newPayload.from_date = null;
    newPayload.to_date = null;
  }

  // Handle board_roles repeater field - convert array of objects to JSON string
  if (newPayload.board_roles && Array.isArray(newPayload.board_roles)) {
    newPayload.board_roles = JSON.stringify(newPayload.board_roles);
  }

  return newPayload;
};

const cleanupPayloadFields = (payload) => {
  const newPayload = { ...payload };

  if (
    newPayload.mainArea === "undefined" ||
    newPayload.mainArea === "null" ||
    !newPayload.mainArea
  ) {
    delete newPayload.mainArea;
  }

  if (
    !newPayload.domains ||
    newPayload.domains === "undefined" ||
    newPayload.domains === "null" ||
    newPayload.domains === null
  ) {
    delete newPayload.domains;
  }

  if (
    newPayload.language === "undefined" ||
    newPayload.language === "null" ||
    !newPayload.language
  ) {
    delete newPayload.language;
  }

  return newPayload;
};

const handleResumeInPayload = (payload, selectedResume) => {
  const newPayload = { ...payload };
  if (
    selectedResume &&
    selectedResume instanceof File &&
    selectedResume.name &&
    selectedResume.size > 0 &&
    selectedResume.type === "application/pdf"
  ) {
    newPayload.resume = selectedResume;
  } else if ("resume" in newPayload) {
    const { resume, ...rest } = newPayload;
    return rest;
  }
  return newPayload;
};

const handleImageUpload = (formData, payload, selectedFile) => {
  if (payload.image instanceof FileList && payload.image.length > 0) {
    formData.append("image", payload.image[0]);
  } else if (payload.image instanceof File) {
    formData.append("image", payload.image);
  } else if (selectedFile instanceof File) {
    formData.append("image", selectedFile);
  }

  if (
    "image" in payload &&
    !(payload.image instanceof File || payload.image instanceof FileList)
  ) {
    delete payload.image;
  }
};

const extractAllowedKeys = (registerStepFields) => {
  const registerStep =
    Array.isArray(registerStepFields) && registerStepFields.length > 0
      ? registerStepFields[0]?.reg_feilds || []
      : [];

  return registerStep.map((field) => field.reg_key);
};

const buildMergedKeys = (allowedKeys) => {
  let mergedKeys = [
    ...allowedKeys,
    "step_number",
    "type",
    "role_type",
    "email_verify_string",
  ];

  if (allowedKeys.includes("domains")) {
    mergedKeys.push("subareas", "subarea", "mainArea");
  }

  if (allowedKeys.includes("availability")) {
    mergedKeys.push("from_date", "to_date");
  }

  const hasBoardRoleFields =
    allowedKeys.includes("rep_company_name") &&
    allowedKeys.includes("rep_board_title") &&
    allowedKeys.includes("rep_start_date") &&
    allowedKeys.includes("rep_end_date");

  if (hasBoardRoleFields) {
    mergedKeys.push("board_roles");
  }

  return mergedKeys;
};

const normalizeBoardArrayFields = (payload) => {
  const boardArrayFields = [
    "board_entity_type",
    "board_entity_type_2",
    "course_completed",
    "accredited_director",
    "indicate_board_experience_level",
    "past_board_roles",
  ];

  for (const fieldKey of boardArrayFields) {
    if (Array.isArray(payload[fieldKey])) {
      payload[fieldKey] = payload[fieldKey].join(",");
    }
  }
};

const isValidValue = (value) =>
  value !== undefined && value !== null && value !== "" && value !== "undefined";

const appendPayloadToFormData = (formData, keys, payload) => {
  for (const key of keys) {
    const value = payload[key];

    if (!isValidValue(value)) continue;

    if (key === "board_roles" && Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  }
};

export const buildStudentSignupFormData = ({
  data,
  registerStepFields,
  selectedFile,
}) => {
  const formData = new FormData();
  const payload = { ...data };

  handleImageUpload(formData, payload, selectedFile);

  const allowedKeys = extractAllowedKeys(registerStepFields);
  const mergedKeys = buildMergedKeys(allowedKeys);

  normalizeBoardArrayFields(payload);

  appendPayloadToFormData(formData, mergedKeys, payload);

  return formData;
};

export const applyVerificationDataImmediately = ({
  data,
  setValue,
  setRoleId,
  handleChange,
  setEmail,
}) => {
  setValue("role_id", data?.role_id);
  setRoleId(data?.role_id);
  handleChange(data?.role_id);
  setEmail(data?.email);
  setValue("email", data?.email);
};

const handleImage = (image, placeholderImage, setSelectedFile, setImg, setValue, clearErrors) => {
  setSelectedFile(image);

  setImg({
    src: image || placeholderImage || null,
    alt: "Upload an Image",
  });

  if (image && typeof image === "string") {
    setValue("image", image, { shouldValidate: false });
    clearErrors("image");
  }
};

const handleResume = (resume, setValue, setResumeText, setSelectedResume) => {
  if (!resume) return;

  setValue("resume", resume);
  setResumeText(resume.split("/").pop());
  setSelectedResume(resume);
};

const handleBooleanFields = (data, setValue, clearErrors) => {
  const booleanFields = [
    "mn_open_to_talk",
    "mn_loking_for_coach",
    "mf_mental_health",
    "mf_certified_coach",
  ];

  for (const field of booleanFields) {
    if (data[field] !== undefined) {
      setValue(field, !!Number(data[field]));
      clearErrors(field);
    }
  }
};

const handleLanguage = (data, setValue) => {
  if (data.language) {
    setValue("language", data.language.split(","));
  }
};

const handleBoardFields = (data, setValue, clearErrors) => {
  const boardFields = {
    board_entity_type: false,
    board_entity_type_2: false,
    course_completed: true,
    accredited_director: true,
    indicate_board_experience_level: true,
    past_board_roles: false,
  };

  for (const key in boardFields) {
    if (!data[key]) continue;

    const values = data[key]
      .split(",")
      .map((v) =>
        boardFields[key] ? Number.parseInt(v.trim()) : v.trim()
      );

    setValue(key, values);
    clearErrors(key);
  }
};

const handleBoardRoles = (data, setValue, clearErrors) => {
  const boardRolesData = data.board_roles_data;
  if (!boardRolesData) return;

  try {
    const roles =
      typeof boardRolesData === "string"
        ? JSON.parse(boardRolesData)
        : boardRolesData;

    const formattedRoles = roles.map((role) => ({
      rep_company_name: role.rep_company_name || "",
      rep_board_title: role.rep_board_title || "",
      rep_start_date: role.rep_start_date || "",
      rep_end_date: role.rep_end_date || "",
      is_currently_working: !!role.is_currently_working,
    }));

    setValue("board_roles", formattedRoles);
    clearErrors("board_roles");
  } catch (error) {
    console.error("Error parsing board_roles:", error);
    setValue("board_roles", []);
  }
};

const handleAvailability = (data, setValue, setShowDateDiv, checkFromDate, checkToDate) => {
  if (data.availability === 2) {
    setValue("availability", true);
    setShowDateDiv("block");

    setValue("from_date", checkFromDate(data.from_date));
    setValue("to_date", checkToDate(data.to_date));
    return;
  }

  setValue("availability", false);
  setValue("from_date", null);
  setValue("to_date", null);
  setShowDateDiv("none");
};

const handleDob = (data, setValue, setDob) => {
  if (!data.dob) return;

  setValue("dob", data.dob);
  setDob(data.dob);
};

const handleSimpleFields = (data, setValue) => {
  const simpleFields = [
    "linkedin", "first_name", "last_name", "number", "university_partners_id",
    "program_study_id", "year_of_graduation", "organization", "position",
    "experience_levels", "about", "is_terms", "is_privacy", "industry_id",
    "training_video", "years_of_experience", "current_course", "gender",
    "country", "lgbtq_discussion", "topics", "my_consent", "any_particular_program",
    "frequency_communication", "employee", "sim_student_id", "programmes",
    "number_of_mantees", "years_of_board_experience"
  ];

  for (const field of simpleFields) {
    if (data[field] !== undefined) {
      setValue(field, data[field]);
    }
  }
};

export const applyVerificationDataWithDelay = ({
  data,
  reset,
  setValue,
  clearErrors,
  setSelectedFile,
  setImg,
  setResumeText,
  setSelectedResume,
  setShowDateDiv,
  checkFromDate,
  checkToDate,
  setDob,
  placeholderImage,
}) => {
  if (!data) return;

  const { image, resume, ...rest } = data;

  reset(rest);

  handleImage(image, placeholderImage, setSelectedFile, setImg, setValue, clearErrors);
  handleResume(resume, setValue, setResumeText, setSelectedResume);
  handleBooleanFields(data, setValue, clearErrors);
  handleLanguage(data, setValue);
  handleBoardFields(data, setValue, clearErrors);
  handleBoardRoles(data, setValue, clearErrors);
  handleAvailability(data, setValue, setShowDateDiv, checkFromDate, checkToDate);
  handleDob(data, setValue, setDob);
  handleSimpleFields(data, setValue);
};

export const handlePostVerificationEffects = ({
  data,
  res,
  setting,
  setReadOnly,
  setEmailVerifyString,
  getObjectiveData,
  registerSetting,
  setUserId,
  setPopupMessage,
  setShowPopup,
  history,
  renderGlobalModal,
}) => {
  setReadOnly(true);
  setEmailVerifyString(data?.email_verify_string);
  getObjectiveData(data?.role_id);
  registerSetting(data?.role_id);
  setUserId(data?.id);
  localStorage.setItem("email_verify", data?.email_verify_string);
  setPopupMessage(res?.message);
  setShowPopup(true);

  if (
    setting?.admin_approval_required === 1 &&
    setting?.email_approval_required === 1
  ) {
    if (data?.email_verfication_status === 1) {
      if (data?.role_id !== "" && data?.first_name !== "") {
        if (renderGlobalModal) {
          renderGlobalModal({ message: res?.message });
        }
        history(`/`);
      }
    }
  }
};

export const buildValidationRules = (validations, is_required, reg_key, label = "") => {
  const rules = {};

  for (const validation of validations) {
    switch (validation?.key) {
      case "required":
        if (is_required === 0) {
          break;
        } else {
          rules.required =
            validation.message
              ?.replaceAll(/<[^>]*>/g, "")
              ?.replaceAll("*", "")
              ?.trim() ||
            `${label} is required`;
          break;
        }
      case "min":
      case "minlength":
        rules.minLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "max":
      case "maxlength":
        rules.maxLength = {
          value: validation.length,
          message: validation.message,
        };
        break;
      case "pattern":
        rules.pattern = {
          value: new RegExp(validation?.regex?.slice(1, -1)),
          message: validation.message,
        };
        break;
      case "maxCount":
        rules.validate = (value) => {
          if (Array.isArray(value) && value?.length > validation?.length) {
            return validation?.message;
          }
          return true;
        };
        break;
      case "size":
        rules.validate = {
          ...rules.validate,
          fileSize: (files) =>
            files?.length > 0
              ? files[0]?.size < validation?.length * 1024 ||
              validation?.message ||
              "File size exceeds the allowed limit"
              : true,
        };
        break;
      case "mimes": {
        const allowedMimeTypes =
          reg_key === "resume" || reg_key === "document"
            ? validation?.types?.map((ext) => `application/${ext}`)
            : validation?.types?.map((ext) =>
              ext === "jpg" ? "image/jpeg" : `image/${ext}`
            );
        rules.validate = {
          ...rules.validate,
          fileType: (files) => {
            if (files?.length > 0) {
              return (
                allowedMimeTypes.includes(files[0].type) ||
                validation.message ||
                "Invalid file type"
              );
            }
            return true;
          },
        };
        break;
      }
      default:
        break;
    }
  }
  return rules;
};